/**
 * Client-side image decode pipeline.
 *
 * decodeFile(file) → { width, height, mp, previewBlob }, where all dimensions
 * and the preview are EXIF-orientation-corrected. Routes by magic-byte sniff:
 *   JPEG/PNG/WebP/GIF/AVIF → createImageBitmap (native)
 *   TIFF                   → utif2 (dynamic import)
 *   HEIC/HEIF              → libheif-js (dynamic import)
 *
 * Rejections surface as DecodeError with a friendly, card-ready message:
 *   too large (> maxBytes), too many megapixels (> maxMegapixels), undecodable.
 */

import { seedPricing } from '@rpl/pricing';
import { decodeHeic } from './heic';
import { decodeRaster } from './raster';
import { decodeTiff } from './tiff';
import { makePreviewBlob } from './preview';

export interface DecodeResult {
  width: number;
  height: number;
  /** Megapixels, unrounded (width*height/1e6). */
  mp: number;
  previewBlob: Blob;
}

export type DecodeReason = 'too_large' | 'too_many_megapixels' | 'undecodable' | 'unreadable' | 'pdf';

export class DecodeError extends Error {
  /** Diagnostics for the upload error log (owner 2026-10-19); never shown. */
  sniffed?: string;
  headHex?: string;
  detail?: string;
  constructor(
    readonly reason: DecodeReason,
    /** Friendly, user-facing message for the photo card. */
    message: string,
  ) {
    super(message);
    this.name = 'DecodeError';
  }
}

function withDiagnostics(e: DecodeError, kind: Kind, head: Uint8Array, cause?: unknown): DecodeError {
  e.sniffed = kind;
  e.headHex = Array.from(head, (b) => b.toString(16).padStart(2, '0')).join('');
  if (cause !== undefined) {
    e.detail = cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause);
  }
  return e;
}

export interface DecodeLimits {
  maxBytes: number;
  maxMegapixels: number;
}

const DEFAULT_LIMITS: DecodeLimits = {
  maxBytes: seedPricing.limits.maxFileBytes,
  maxMegapixels: seedPricing.limits.maxDecodeMegapixels,
};

type Kind = 'jpeg' | 'png' | 'webp' | 'gif' | 'tiff' | 'heic' | 'avif' | 'pdf' | 'unknown';

function ascii(bytes: Uint8Array, start: number, len: number): string {
  let s = '';
  for (let i = start; i < start + len && i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return s;
}

/** Detect container from the first bytes. */
export function sniff(head: Uint8Array): Kind {
  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'jpeg';
  if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return 'png';
  if (head[0] === 0x47 && head[1] === 0x49 && head[2] === 0x46) return 'gif';
  if (ascii(head, 0, 4) === '%PDF') return 'pdf';
  if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 4) === 'WEBP') return 'webp';
  if (
    // Classic TIFF: II*\0 (little-endian) / MM\0* (big-endian)
    (head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2a && head[3] === 0x00) ||
    (head[0] === 0x4d && head[1] === 0x4d && head[2] === 0x00 && head[3] === 0x2a) ||
    // BigTIFF: II+\0 / MM\0+ — the ".tif variant" fix (version 43)
    (head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2b && head[3] === 0x00) ||
    (head[0] === 0x4d && head[1] === 0x4d && head[2] === 0x00 && head[3] === 0x2b)
  ) {
    return 'tiff';
  }
  if (ascii(head, 4, 4) === 'ftyp') {
    const brand = ascii(head, 8, 4).toLowerCase();
    if (brand === 'avif' || brand === 'avis') return 'avif';
    if (brand.startsWith('hei') || brand.startsWith('hev') || brand === 'mif1' || brand === 'msf1') {
      return 'heic';
    }
  }
  return 'unknown';
}

const UNDECODABLE_MSG = "We couldn't read this file. We accept JPEG, PNG, TIFF and HEIC.";
export const PDF_MSG = "PDFs can't be printed from here. Save it as a JPEG or TIFF and upload that.";
/** The device could not open the picked file at all (owner 2026-10-20: an
 *  Android customer's 597 Google Photos picks all failed NotReadableError). */
export const UNREADABLE_MSG =
  "Your device couldn't open this photo. If it's in Google Photos or iCloud, download it to your device first.";

/** Read the first bytes, retrying once: a picked file can be briefly
 *  unreadable while the phone fetches it. */
async function readHead(file: File): Promise<Uint8Array> {
  try {
    return new Uint8Array(await file.slice(0, 16).arrayBuffer());
  } catch {
    await new Promise((r) => setTimeout(r, 400));
    return new Uint8Array(await file.slice(0, 16).arrayBuffer());
  }
}

async function decodeToBitmap(file: File, kind: Kind): Promise<ImageBitmap> {
  switch (kind) {
    case 'tiff':
      return decodeTiff(file);
    case 'heic':
      return decodeHeic(file);
    case 'jpeg':
    case 'png':
    case 'webp':
    case 'gif':
    case 'avif':
      return decodeRaster(file);
    default:
      // Last resort: some browsers still decode exotic types natively.
      return decodeRaster(file);
  }
}

export async function decodeFile(
  file: File,
  limits: DecodeLimits = DEFAULT_LIMITS,
): Promise<DecodeResult> {
  if (file.size > limits.maxBytes) {
    const mb = Math.round(limits.maxBytes / (1024 * 1024));
    throw new DecodeError(
      'too_large',
      `This file is over ${mb} MB. Email us for custom orders.`,
    );
  }

  let head: Uint8Array;
  try {
    head = await readHead(file);
  } catch (err) {
    throw withDiagnostics(new DecodeError('unreadable', UNREADABLE_MSG), 'unknown', new Uint8Array(0), err);
  }
  const kind = sniff(head);
  if (kind === 'pdf') throw withDiagnostics(new DecodeError('pdf', PDF_MSG), kind, head);

  let bitmap: ImageBitmap;
  try {
    bitmap = await decodeToBitmap(file, kind);
  } catch (err) {
    // Surface the underlying decoder error for dev diagnosis (the card only
    // shows the friendly message).
    console.warn(`[decode] ${kind} decode failed for "${file.name}":`, err);
    throw withDiagnostics(new DecodeError('undecodable', UNDECODABLE_MSG), kind, head, err);
  }

  const { width, height } = bitmap;
  if (!width || !height) {
    bitmap.close?.();
    throw withDiagnostics(new DecodeError('undecodable', UNDECODABLE_MSG), kind, head, 'zero-size bitmap');
  }

  const mp = (width * height) / 1_000_000;
  if (mp > limits.maxMegapixels) {
    bitmap.close?.();
    throw withDiagnostics(
      new DecodeError(
        'too_many_megapixels',
        `This file is very large (${Math.round(mp)} MP). Save a copy at 10,000 pixels on the long side and upload that. It's still plenty for prints up to A0.`,
      ),
      kind,
      head,
      `${width}x${height}`,
    );
  }

  const previewBlob = await makePreviewBlob(bitmap);
  bitmap.close?.();
  return { width, height, mp, previewBlob };
}
